import { defineStore } from 'pinia';
import { showBrowserNotification } from 'dashboard/helper/browserNotification';
import HubNotificationsAPI from 'dashboard/api/hubNotifications';

// Notifications Hub — backed by the Rails API
// (/api/v1/accounts/:id/hub_notifications). Server-side events (reminders
// firing, automations) are created server-side and pushed over ActionCable
// (`hub.notification.created` → addFromBroadcast); client-side events (chat,
// manual logs) POST through `log`. The browser-notification preference stays in
// localStorage (a per-device UI setting).

const PREFS_KEY = 'flowdots_notifications_prefs';

export const NOTIFICATION_CATEGORIES = [
  'reminder',
  'event',
  'chat',
  'label',
  'system',
];

export const EXECUTOR_TYPES = ['user', 'automation', 'contact', 'system'];

const loadPrefs = () => {
  try {
    return (
      JSON.parse(localStorage.getItem(PREFS_KEY)) || { browserEnabled: false }
    );
  } catch {
    return { browserEnabled: false };
  }
};

const persistPrefs = prefs => {
  try {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
  } catch {
    // ignore
  }
};

const toItem = n => ({
  id: n.id,
  timestamp:
    n.timestamp || (n.created_at ? new Date(n.created_at).getTime() : Date.now()),
  read: n.read,
  category: n.category,
  action: n.action,
  title: n.title,
  body: n.body,
  executor: n.executor || { type: 'system', name: 'Sistema' },
  targetUserIds: n.target_user_ids || n.targetUserIds || [],
  meta: n.meta || {},
});

const toApi = payload => ({
  category: payload.category || 'system',
  action: payload.action || '',
  title: payload.title || '',
  body: payload.body || '',
  executor_type: payload.executor?.type || 'system',
  executor_name: payload.executor?.name || 'Sistema',
  executor_id: payload.executor?.id,
  target_user_ids: payload.targetUserIds || [],
  meta: payload.meta || {},
});

export const useNotificationsHubStore = defineStore('notificationsHub', {
  state: () => ({
    items: [],
    prefs: loadPrefs(),
    currentUserId: null,
  }),
  getters: {
    list: state => [...state.items].sort((a, b) => b.timestamp - a.timestamp),
    unreadCount: state => state.items.filter(i => !i.read).length,
    browserEnabled: state => state.prefs.browserEnabled,
  },
  actions: {
    setCurrentUser(id) {
      this.currentUserId = id ?? null;
    },
    async fetch() {
      const { data } = await HubNotificationsAPI.get();
      this.items = (data.payload || []).map(toItem);
    },
    // Client-originated log (chat events, manual activity). Persists via the API.
    async log(payload) {
      const { data } = await HubNotificationsAPI.create(toApi(payload));
      const entry = toItem(data.payload);
      this.items = [entry, ...this.items];
      this.maybeBrowserNotify(entry);
      return entry;
    },
    // Server push over ActionCable. Dedups by id.
    addFromBroadcast(notification) {
      const entry = toItem(notification);
      if (this.items.some(i => i.id === entry.id)) return;
      this.items = [entry, ...this.items];
      this.maybeBrowserNotify(entry);
    },
    maybeBrowserNotify(entry) {
      // Automations are logged (and live-update the hub) but never raise a
      // browser/OS notification.
      if (entry.executor?.type === 'automation') return;
      const aimedAtCurrentUser =
        this.currentUserId != null &&
        entry.targetUserIds.some(
          id => String(id) === String(this.currentUserId)
        );
      if (this.prefs.browserEnabled && aimedAtCurrentUser) {
        showBrowserNotification(entry.title, {
          body: entry.body,
          tag: String(entry.id),
        });
      }
    },
    async markRead(id) {
      this.items = this.items.map(i => (i.id === id ? { ...i, read: true } : i));
      await HubNotificationsAPI.update(id, { read: true });
    },
    async markAllRead() {
      this.items = this.items.map(i => ({ ...i, read: true }));
      await HubNotificationsAPI.markAllRead();
    },
    async remove(id) {
      this.items = this.items.filter(i => i.id !== id);
      await HubNotificationsAPI.delete(id);
    },
    async clear() {
      this.items = [];
      await HubNotificationsAPI.clear();
    },
    setBrowserEnabled(enabled) {
      this.prefs = { ...this.prefs, browserEnabled: enabled };
      persistPrefs(this.prefs);
    },
  },
});
